import { useContext, useEffect, useRef, useState } from 'react';
import { MobyChatContext } from '@/components/provider';
import type { MediaSession } from '@/lib/av-stream-types';
import type { RemotePeerProfile } from '@/lib/peer-profile-utils';
import { diagSummary, snapshotSession } from '@/lib/star-topology-instrumentation';

/**
 * Extended media session with peer context for UI display
 */
export interface MediaSessionWithContext {
  sessionId: string;
  session: MediaSession;
  peerAddress: string;
  contactName: string;
  contactInitials: string;
  contactProfile?: RemotePeerProfile;
  // Group call metadata for UI
  isGroupCall?: boolean;
  groupName?: string;
  groupAddress?: string;
  groupParticipantCount?: number;
}

/**
 * Hook to access active media sessions from all peers
 * Returns sessions that are active, connecting, or ringing
 */
export function useMediaSessions(): MediaSessionWithContext[] {
  const context = useContext(MobyChatContext);
  const [sessions, setSessions] = useState<MediaSessionWithContext[]>([]);
  
  if (!context) {
    throw new Error('useMediaSessions must be used within a MobyChatProvider');
  }
  
  const { node, db } = context;
  
  // PERF FIX: Fingerprint to avoid unnecessary setState when sessions haven't changed
  const lastFingerprintRef = useRef('');

  useEffect(() => {
    let mounted = true;
    
    const updateSessions = async () => {
      if (!mounted) return;
      
      const activeSessions: MediaSessionWithContext[] = [];
      
      // Use global registry for O(1) session access if available
      if (node.globalMediaRegistry) {
        const allSessions = node.globalMediaRegistry.getAllSessions();
        for (const [sessionId, mediaSession] of allSessions) {
          // Only show active, connecting, ringing, or offering sessions
          if (
            mediaSession.state === 'active' ||
            mediaSession.state === 'ringing' ||
            mediaSession.state === 'connecting' ||
            mediaSession.state === 'offering'
          ) {
            // GROUP CALL UX: Emit a SINGLE entry with group metadata so the UI
            // renders one card instead of N peer cards, in these pre-accept cases:
            //   - 'ringing'  (receiver side, pre-accept)
            //   - 'offering' (organizer outgoing, pre-accept)
            //
            // Phase 2A bug fix: active leaf group sessions no longer fall into
            // this single-tile path. Phase 2A's GROUP_AV_TRACK_ORIGIN attribution
            // keys remoteStreams by originator address, so per-peer rendering
            // (the else branch below) now correctly shows one tile per other
            // participant. The old `isLeafActive` consolidation was a workaround
            // from before origin attribution and is now incorrect — it caused
            // leaves to render only the hub's tile even when forwarded tracks
            // for other leaves were live in session.remoteStreams.
            if (mediaSession.isGroupCall && (mediaSession.state === 'ringing' || mediaSession.state === 'offering')) {
              const initiator = mediaSession.peerAddresses[0]
                || node.globalMediaRegistry.getPeerForSession(sessionId) || '';
              const contact = initiator ? await db.contacts.get({ address: initiator }) : undefined;

              // Look up group name from DB
              let groupName: string | undefined;
              if (mediaSession.groupAddress) {
                try {
                  const chat = await db.chats.get({
                    type: 1, // MessageDestinationType.GROUP
                    address: mediaSession.groupAddress
                  });
                  groupName = chat?.name;
                } catch { /* swallow */ }
              }

              activeSessions.push({
                sessionId,
                session: mediaSession,
                peerAddress: initiator,
                contactName: contact?.getDisplayName() || initiator.slice(0, 8),
                contactInitials: contact?.getInitials() || initiator.slice(0, 2).toUpperCase(),
                contactProfile: contact?.custom?.profile,
                isGroupCall: true,
                groupName: groupName || `Group (${mediaSession.peerAddresses.length + 1} members)`,
                groupAddress: mediaSession.groupAddress,
                groupParticipantCount: mediaSession.peerAddresses.length
              });
            } else {
              // P2P calls or active/connecting group calls — show all peers
              const peersToShow = mediaSession.peerAddresses.length > 0 
                ? mediaSession.peerAddresses 
                : [node.globalMediaRegistry.getPeerForSession(sessionId) || ''];
              
              for (const peerAddress of peersToShow) {
                if (!peerAddress) continue;
                
                const contact = await db.contacts.get({ address: peerAddress });
                
                activeSessions.push({
                  sessionId,
                  session: mediaSession,
                  peerAddress,
                  contactName: contact?.getDisplayName() || peerAddress.slice(0, 8),
                  contactInitials: contact?.getInitials() || peerAddress.slice(0, 2).toUpperCase(),
                  contactProfile: contact?.custom?.profile,
                  isGroupCall: mediaSession.isGroupCall,
                  groupAddress: mediaSession.groupAddress
                });
              }
            }
          }
        }
      } else {
        // Fallback: iterate through peers (legacy behavior)
        // Track already-emitted ringing group sessions to avoid N duplicate cards
        const emittedRingingSessions = new Set<string>();
        const peers = Array.from(node.peers.values());
        for (const peer of peers) {
          for (const [sessionId, mediaSession] of peer.mediaSessions.entries()) {
            if (
              mediaSession.state === 'active' ||
              mediaSession.state === 'ringing' ||
              mediaSession.state === 'connecting' ||
              mediaSession.state === 'offering'
            ) {
              // GROUP CALL UX: Emit only one entry for ringing/offering group
              // calls. Phase 2A bug fix: active leaf sessions now render per-peer
              // (see primary branch above for rationale).
              if (mediaSession.isGroupCall && (mediaSession.state === 'ringing' || mediaSession.state === 'offering')) {
                if (emittedRingingSessions.has(sessionId)) continue;
                emittedRingingSessions.add(sessionId);

                const initiator = mediaSession.peerAddresses[0] || peer.address;
                const contact = await db.contacts.get({ address: initiator });
                let groupName: string | undefined;
                if (mediaSession.groupAddress) {
                  try {
                    const chat = await db.chats.get({
                      type: 1, // MessageDestinationType.GROUP
                      address: mediaSession.groupAddress
                    });
                    groupName = chat?.name;
                  } catch { /* swallow */ }
                }

                activeSessions.push({
                  sessionId,
                  session: mediaSession,
                  peerAddress: initiator,
                  contactName: contact?.getDisplayName() || initiator.slice(0, 8),
                  contactInitials: contact?.getInitials() || initiator.slice(0, 2).toUpperCase(),
                  contactProfile: contact?.custom?.profile,
                  isGroupCall: true,
                  groupName: groupName || `Group (${mediaSession.peerAddresses.length + 1} members)`,
                  groupAddress: mediaSession.groupAddress,
                  groupParticipantCount: mediaSession.peerAddresses.length
                });
              } else {
                const contact = await db.contacts.get({ address: peer.address });
                
                activeSessions.push({
                  sessionId,
                  session: mediaSession,
                  peerAddress: peer.address,
                  contactName: contact?.getDisplayName() || peer.address.slice(0, 8),
                  contactInitials: contact?.getInitials() || peer.address.slice(0, 2).toUpperCase(),
                  contactProfile: contact?.custom?.profile,
                  isGroupCall: mediaSession.isGroupCall,
                  groupAddress: mediaSession.groupAddress
                });
              }
            }
          }
        }
      }
      
      if (mounted) {
        // PERF FIX: Only update state if sessions actually changed
        const fingerprint = JSON.stringify(activeSessions.map(s => s.sessionId + ':' + s.session.state + ':' + s.peerAddress));
        if (fingerprint !== lastFingerprintRef.current) {
          lastFingerprintRef.current = fingerprint;

          // STAR_DIAG: render-time anomaly detection.
          // (A) Multiple sessions per group → duplicate rendering.
          // (B) isGroupCall session with peerCount <= 1 → leaf with stunted roster.
          // Both are rare, so logging is cheap and only fires when a real problem exists.
          const byGroup = new Map<string, string[]>();
          const stuntedGroupSessions: ReturnType<typeof snapshotSession>[] = [];
          const uniqueSessionIds = new Set<string>();
          for (const entry of activeSessions) {
            if (uniqueSessionIds.has(entry.sessionId)) continue;
            uniqueSessionIds.add(entry.sessionId);
            const s = entry.session;
            if (s.isGroupCall && s.groupAddress) {
              const arr = byGroup.get(s.groupAddress) ?? [];
              arr.push(entry.sessionId);
              byGroup.set(s.groupAddress, arr);
            }
            if (s.isGroupCall && s.peerAddresses.length <= 1 && s.state === 'active') {
              stuntedGroupSessions.push(snapshotSession(s));
            }
          }
          for (const [groupAddr, ids] of byGroup) {
            if (ids.length > 1) {
              diagSummary('render-duplicate-sessions-per-group', {
                groupAddress: groupAddr.slice(0, 8),
                sessionIds: ids.map(i => i.slice(0, 8)),
                count: ids.length,
              });
            }
          }
          if (stuntedGroupSessions.length > 0) {
            diagSummary('render-stunted-group-session', {
              count: stuntedGroupSessions.length,
              sessions: stuntedGroupSessions,
            });
          }

          setSessions(activeSessions);
        }
      }
    };
    
    // Update immediately
    updateSessions();
    
    // Poll for updates every 2 seconds (fallback; events handle most updates)
    const interval = setInterval(updateSessions, 2000);
    
    // AV FIX C: Also refresh immediately on AV lifecycle events so the UI
    // responds within a single frame instead of waiting up to the poll interval.
    const immediateRefresh = () => { updateSessions(); };
    const avEvents = [
      'av-session-reconnected',
      'av-session-disconnected',
      'av-remote-stream-added',
      'av-call-accepted',
      'av-call-ended',
      'av-participant-left'
    ];
    avEvents.forEach(evt => window.addEventListener(evt, immediateRefresh));
    
    return () => {
      mounted = false;
      clearInterval(interval);
      avEvents.forEach(evt => window.removeEventListener(evt, immediateRefresh));
    };
  }, [node, db]);
  
  return sessions;
}

/**
 * Hook to get a specific media session by session ID
 */
export function useMediaSession(sessionId: string | undefined): MediaSessionWithContext | null {
  const sessions = useMediaSessions();
  
  if (!sessionId) return null;
  
  return sessions.find(s => s.sessionId === sessionId) || null;
}

/**
 * Hook to check if any media sessions are active
 */
export function useHasActiveMediaSessions(): boolean {
  const sessions = useMediaSessions();
  return sessions.length > 0;
}
